import type { SymbolInfo } from '@/types';

/**
 * The searchable universe: every index plus each stock that has an exchange-board
 * suffix (JKH.N0000). Bare stock tickers are not listings, so they're dropped.
 */
export function prepareSymbols(symbols: SymbolInfo[]): SymbolInfo[] {
  return symbols
    .filter((s) => s.type !== 'stock' || s.symbol.includes('.'))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/** Ticker without the board suffix: "JKH.N0000" -> "JKH". Indices have none. */
export function baseTicker(symbol: string): string {
  return symbol.split('.')[0];
}

// Lower is a better match; null is no match. An exact ticker beats a ticker
// prefix, which beats a substring of the full symbol, which beats a word (then
// any part) of the company name.
function matchRank(s: SymbolInfo, q: string): number | null {
  const sym = s.symbol.toLowerCase();
  const ticker = baseTicker(sym);
  if (ticker === q) return 0;
  if (ticker.startsWith(q)) return 1;
  if (sym.includes(q)) return 2;

  const name = s.name.toLowerCase();
  const words = name.split(/[\s(),.&/-]+/).filter(Boolean);
  if (words[0]?.startsWith(q)) return 3;
  if (words.some((w) => w.startsWith(q))) return 3.5;
  if (name.includes(q)) return 4;
  return null;
}

interface FilterOptions {
  type?: SymbolInfo['type'];
  /** Symbols to leave out entirely. */
  exclude?: readonly string[];
  /** Order by match quality instead of keeping the input order. */
  rank?: boolean;
  limit?: number;
}

/**
 * Case-insensitive symbol/name search over a prepared list. Without `rank` this
 * is a plain substring match that keeps the input order; with it, results are
 * ordered best match first, then stocks before indices, then shorter symbols.
 * An empty query matches everything.
 */
export function filterSymbols(symbols: SymbolInfo[], query: string, opts: FilterOptions = {}): SymbolInfo[] {
  const { type, exclude, rank = false, limit } = opts;
  const q = query.trim().toLowerCase();

  const scoped = symbols.filter((s) => (!type || s.type === type) && !exclude?.includes(s.symbol));

  let out: SymbolInfo[];
  if (!q) {
    out = scoped;
  } else if (!rank) {
    out = scoped.filter((s) => s.symbol.toLowerCase().includes(q) || s.name.toLowerCase().includes(q));
  } else {
    out = scoped
      .flatMap((s) => {
        const r = matchRank(s, q);
        return r == null ? [] : [{ s, r }];
      })
      .sort(
        (a, b) =>
          a.r - b.r ||
          Number(a.s.type === 'index') - Number(b.s.type === 'index') ||
          a.s.symbol.length - b.s.symbol.length,
      )
      .map(({ s }) => s);
  }
  return limit == null ? out : out.slice(0, limit);
}
